import { MicrophoneIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { Tooltip } from 'components/atoms/Tooltip';
import { useAIChatContext } from 'components/organisms/AIChat/AIChat.context';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactSpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';
import { logAmplitude } from 'utils/amplitude';
import { WEB_AIASSISTANT_CLICKRECORDAUDIO } from 'utils/amplitude/events/ai.amplitude';
import startRecordMp3 from './assets/startRecord.mp3';
import stopRecordMp3 from './assets/stopRecord.mp3';
import useFeatureLock from 'hooks/Subscriptions/useFeatureLock';
import { SubscriptionCounterKeysEnum } from 'types/subscriptions.types';
import { aiAdvisorMonthlyLimitLockContent } from '../PaywallLockV2/locks.content';

const startAudio = new Audio(startRecordMp3);
const stopAudio = new Audio(stopRecordMp3);

const Dictaphone: FC<{ disabled?: boolean; hasMessage?: boolean }> = ({
  disabled,
  hasMessage,
}) => {
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();
  const { setMessage, usage } = useAIChatContext();

  useEffect(() => {
    if (!transcript?.trim().length) return;
    setMessage(transcript);
  }, [transcript, setMessage]);

  const { i18n, t } = useTranslation();

  const { withPaywallCounterLock } = useFeatureLock();

  const onClick = () => {
    if (listening) {
      stopAudio.play();
      ReactSpeechRecognition.stopListening();
    } else {
      resetTranscript();
      startAudio.play();
      ReactSpeechRecognition.startListening({
        language: i18n.language,
        continuous: true,
        interimResults: true,
      });
      logAmplitude(WEB_AIASSISTANT_CLICKRECORDAUDIO);
    }
  };

  if (!browserSupportsSpeechRecognition) {
    return (
      <Tooltip
        content={t('ai_bot.speech_recognition_not_supported')}
        placement="right"
      >
        <div className="text-primary p-2 font-semibold items-center justify-center">
          <MicrophoneIcon className="w-5 h-5" />
        </div>
      </Tooltip>
    );
  }

  // Keep mounted but hide the icon if there's already text and we're not listening
  if (!listening && hasMessage) {
    return null;
  }

  return (
    <Button
      asDefault
      disabled={!!disabled}
      onClick={() =>
        withPaywallCounterLock(
          onClick,
          SubscriptionCounterKeysEnum.taxAdvisorMonthlyMessages,
          aiAdvisorMonthlyLimitLockContent,
        )
      }
      className={classNames(
        'text-primary p-2 font-semibold items-center justify-center',
        {
          'bg-red-500 text-white rounded-full transition-all duration-500 animate-pulse':
            listening,
        },
      )}
    >
      {listening ? (
        <span className="w-3 h-3 mx-1.5 inline-block bg-white rounded-sm" />
      ) : (
        <MicrophoneIcon className="w-5 h-5" />
      )}
    </Button>
  );
};
export default Dictaphone;
